// Triangulate_Pure.ts — a small hand-rolled ear-clipping triangulator for simple polygons
// (Buildings Set1 §4.3). Keeps the geometry layer three-free so it is Node-testable; the
// codebase has no earcut dependency and this is sufficient for the mostly-convex room
// footprints / floor slabs the building system produces (§11 open item 1 notes earcut as a
// drop-in alternative if robustness issues appear).
//
// Input: a CLOSED or open polygon in CCW winding (interior on the left when y+ is up). Output:
// an index triangle list into the SAME vertex array. The polygon must be SIMPLE (no
// self-intersections); reflex vertices are handled. Returns null when the polygon is
// degenerate (< 3 vertices, or area ~0) so callers can skip the face gracefully.
import type {Vec2Simple} from "../../Utils/ThreeJS/VectorStructs.js";

/** Signed area of a polygon (positive = CCW). */
export function PolygonSignedArea2D(points: Vec2Simple[]): number {
	let area = 0;
	for (let i = 0; i < points.length; i++) {
		const [x1, y1] = points[i];
		const [x2, y2] = points[(i + 1) % points.length];
		area += x1 * y2 - x2 * y1;
	}
	return area / 2;
}

function cross2(ax: number, ay: number, bx: number, by: number, cx: number, cy: number): number {
	return (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
}

function inTriangle(px: number, py: number, ax: number, ay: number, bx: number, by: number, cx: number, cy: number): boolean {
	// Point-in-triangle (barycentric, inclusive edges). Works for either winding since we
	// compare same-signed cross products.
	const d1 = cross2(px, py, ax, ay, bx, by);
	const d2 = cross2(px, py, bx, by, cx, cy);
	const d3 = cross2(px, py, cx, cy, ax, ay);
	const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
	const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
	return !(hasNeg && hasPos);
}

/** Triangulate `points` (assumed CCW + simple) into an index array of triangle triples. Returns
 *  null for degenerate input. The polygon may be passed open (last != first) — a closing edge is
 *  implied. */
export function Triangulate2D(points: Vec2Simple[]): number[]|n {
	const n = points.length;
	if (n < 3) return null;
	// Work on a mutable ring of indices; strip a trailing duplicate of the first point.
	const indices: number[] = [];
	for (let i = 0; i < n; i++) indices.push(i);
	// If the polygon is explicitly closed (last == first), drop the duplicate so the ring has
	// `n-1` real vertices.
	if (indices.length > 1) {
		const a = points[indices[0]], b = points[indices[indices.length - 1]];
		if (Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9) indices.pop();
	}
	if (indices.length < 3) return null;
	if (Math.abs(PolygonSignedArea2D(indices.map(i=>points[i]))) < 1e-12) return null;

	const tris: number[] = [];
	const ring = indices.slice();
	let guard = ring.length * ring.length * 2 + 10;
	let prevLen = -1;
	while (ring.length > 3) {
		// Safety guard: if an iteration makes no progress (shouldn't happen for simple
		// polygons, but protects against a degenerate reflex cluster), bail.
		if (ring.length == prevLen) {
			if (--guard < 0) return null;
		} else {
			prevLen = ring.length;
			guard = ring.length * ring.length * 2 + 10;
		}

		let earFound = false;
		for (let i = 0; i < ring.length; i++) {
			const a = ring[(i - 1 + ring.length) % ring.length];
			const b = ring[i];
			const c = ring[(i + 1) % ring.length];
			const [ax, ay] = points[a];
			const [bx, by] = points[b];
			const [cx, cy] = points[c];
			// Ear if convex (cross product positive for CCW) AND no other vertex is inside.
			const cr = cross2(ax, ay, bx, by, cx, cy);
			if (cr <= 1e-12) continue; // reflex or collinear → not an ear
			let contains = false;
			for (let j = 0; j < ring.length; j++) {
				const k = ring[j];
				if (k == a || k == b || k == c) continue;
				const [px, py] = points[k];
				if (inTriangle(px, py, ax, ay, bx, by, cx, cy)) { contains = true; break; }
			}
			if (contains) continue;
			// Clip the ear.
			tris.push(a, b, c);
			ring.splice(i, 1);
			earFound = true;
			break;
		}
		if (!earFound) return null; // should not happen for a simple polygon
	}
	// Final triangle.
	tris.push(ring[0], ring[1], ring[2]);
	return tris;
}
